// Domain types for the internal messaging system.
// These mirror the shapes returned by the messaging RPCs in
// supabase/migrations/20260601000001_messaging_core.sql. They are
// hand-typed (not generated) because supabase gen types runs against
// the live project; once the migration ships and types are regenerated,
// the Database type will include these and we can swap to those generics.

export type ConversationType = 'direct' | 'group' | 'broadcast';
export type AudienceKind = 'all_team' | 'all_leadership' | 'corporate_only';
export type AppRole = 'super_admin' | 'admin' | 'corporate' | 'lead_manager' | 'manager' | 'employee';

export interface Conversation {
  id: string;
  company_id: string;
  type: ConversationType;
  name: string | null;
  audience_kind: AudienceKind | null;
  audience_location_id: string | null;
  allow_replies: boolean;
  created_by: string;
  created_at: string;
  last_message_at: string;
}

export interface ConversationListEntry {
  conversation_id: string;
  type: ConversationType;
  name: string | null;
  audience_kind: AudienceKind | null;
  audience_location_id: string | null;
  allow_replies: boolean;
  created_by: string;
  last_message_at: string;
  last_message_preview: string | null;
  last_message_sender_id: string | null;
  last_message_sender_name: string | null;
  unread_count: number;
  muted_until: string | null;
  archived_at: string | null;
  member_count: number;
  other_profile_id: string | null;
  other_display_name: string | null;
  // First names of the other members (max 6), populated only for
  // groups without a title so clients can label them by membership.
  member_first_names?: string[] | null;
}

export interface UnreadSummaryRow {
  conversation_id: string;
  unread_count: number;
  last_message_at: string;
}

export interface ConversationMember {
  profile_id: string;
  display_name: string;
  role: AppRole;
  joined_at: string;
  last_read_at: string | null;
  left_at: string | null;
  /** false once the profile is deactivated or deleted ("Former Employee").
   *  Optional: absent from servers that predate it, and absent means active.
   *  Use isCurrentMember() from '@/lib/messaging/current-members'. */
  is_active?: boolean;
}

export interface MessageAttachment {
  id: string;
  message_id: string;
  storage_path: string;
  mime_type: string;
  width: number | null;
  height: number | null;
  size_bytes: number;
  created_at: string;
}

export interface Message {
  id: string;
  conversation_id: string;
  sender_id: string;
  body: string;
  created_at: string;
  edited_at: string | null;
  deleted_at: string | null;
  deleted_by: string | null;
  reply_to_id: string | null;
}

export interface ReplyPreview {
  id: string;
  sender_id: string;
  body: string;
  deleted_at: string | null;
}

export interface MessageReaction {
  id: string;
  message_id: string;
  profile_id: string;
  emoji: string;
  created_at: string;
}

export interface MessageReadReceipt {
  message_id: string;
  profile_id: string;
  read_at: string;
}

export interface MessageWithAttachments extends Message {
  attachments: MessageAttachment[];
  reactions?: MessageReaction[];
  read_receipts?: MessageReadReceipt[];
  reply_to?: ReplyPreview | null;
  // Client-only: set on a bubble this device painted before the server
  // confirmed it (see src/lib/messaging/optimistic-messages.ts). Never sent
  // by the server; a server row replacing ours drops both fields.
  local_status?: 'sending' | 'sent';
  /** Files still uploading with a 'sending' bubble. We never fake their URLs. */
  pending_attachment_count?: number;
}

export interface ReactionGroup {
  emoji: string;
  count: number;
  reactorProfileIds: string[];
  mineSelected: boolean;
}

export const groupReactions = (
  reactions: MessageReaction[] | undefined,
  viewerProfileId: string,
): ReactionGroup[] => {
  if (!reactions || reactions.length === 0) return [];
  const map = new Map<string, ReactionGroup>();
  for (const r of reactions) {
    const g = map.get(r.emoji);
    if (g) {
      g.count += 1;
      g.reactorProfileIds.push(r.profile_id);
      if (r.profile_id === viewerProfileId) g.mineSelected = true;
    } else {
      map.set(r.emoji, {
        emoji: r.emoji,
        count: 1,
        reactorProfileIds: [r.profile_id],
        mineSelected: r.profile_id === viewerProfileId,
      });
    }
  }
  return Array.from(map.values()).sort((a, b) => b.count - a.count || a.emoji.localeCompare(b.emoji));
};

export interface MessageableProfile {
  profile_id: string;
  display_name: string;
  role: AppRole;
  location_name: string | null;
  /**
   * Whether this person can hold an authenticated session (profiles.user_id is
   * set). PIN-only staff cannot, and a mobile-surface thread is invisible to
   * them by design — see useMessageablePeople for why this is attached here.
   * Undefined when the caller did not resolve it.
   */
  has_login?: boolean;
}

export interface AudiencePreviewEntry {
  profile_id: string;
  display_name: string;
  role: AppRole;
}

export interface AttachmentUploadInput {
  storagePath: string;
  mimeType: string;
  sizeBytes: number;
  width?: number;
  height?: number;
}

export interface SendMessageInput {
  conversationId: string;
  body: string;
  attachments?: AttachmentUploadInput[];
  replyToId?: string | null;
}

// Total unread count derived from UnreadSummaryRow[].
export const totalUnread = (rows: UnreadSummaryRow[] | undefined): number =>
  rows ? rows.reduce((acc, r) => acc + (r.unread_count || 0), 0) : 0;

// "Maya, Aria & Devon" / "Maya, Aria, Devon, Lily +2" — label for a
// group that was created without a title. `names` carries at most the
// first 6 other members; member_count covers the long tail.
export const formatMemberNames = (
  names: string[] | null | undefined,
  memberCount: number,
): string | null => {
  if (!names || names.length === 0) return null;
  const MAX_SHOWN = 4;
  const othersTotal = Math.max(memberCount - 1, names.length);
  const shown = names.slice(0, MAX_SHOWN);
  const remaining = othersTotal - shown.length;
  if (remaining > 0) return `${shown.join(', ')} +${remaining}`;
  if (shown.length === 1) return shown[0];
  return `${shown.slice(0, -1).join(', ')} & ${shown[shown.length - 1]}`;
};

export const conversationDisplayTitle = (c: ConversationListEntry): string => {
  if (c.type === 'direct') return c.other_display_name ?? 'Direct message';
  if (c.type === 'group') {
    return c.name ?? formatMemberNames(c.member_first_names, c.member_count) ?? 'Group';
  }
  if (c.type === 'broadcast') {
    switch (c.audience_kind) {
      case 'all_team':
        return c.name ? `All Team — ${c.name}` : 'All Team Members';
      case 'all_leadership':
        return c.name ? `Leadership — ${c.name}` : 'All Leadership Team';
      case 'corporate_only':
        return c.name ?? 'Corporate Only';
      default:
        return c.name ?? 'Broadcast';
    }
  }
  return 'Conversation';
};

export const isBroadcastReadOnly = (c: ConversationListEntry, viewerProfileId: string): boolean =>
  c.type === 'broadcast' && !c.allow_replies && c.created_by !== viewerProfileId;
